import { Form, Formik } from 'formik';
import { useState, useRef, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import Button from '../../components/Button';
import ButtonBack from '../../components/ButtonBack';
import { FormGroup } from '../../components/Form';
import PasswordRules from '../../components/PasswordRules';
import Spacing from '../../components/Spacing';
import Text from '../../components/Text';
import schemaPassword from '../../utils/schemas/password';
import { Container, OutlinedInputContainer, ErrorText } from './styles';
import ErrorMessage from '../../components/Form/ErrorMessage';
import { logout, resetPassword, validatePasswordCode } from '../../services/user';
import Modal from './components/Modal';
import Input from '../../components/Form/Input';

const SubmitButton = styled(Button)`
  width: 100%;
`;

const Main = (props) => {

  const history = useHistory();
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);

  const credentials = useSelector(state => state.main.credentials);

  const [passwordCriteria, setPasswordCriteria] = useState({
    uppercase: false,
    lowercase: false,
    number: false,
    length: false
  });

  const ref = useRef(null); // Create a reference for the Formik component

  const validateCode = async (code) => {
    try {
      const { token } = await validatePasswordCode({
        [credentials.field]: credentials.value,
      }, code);
      return token;
    } catch (err) {
      setError('Código incorreto, verifique novamente.');
      return null;
    }
  }

  const handleSubmit = async (formData, { setSubmitting, isSubmitting }) => {
    if (isSubmitting || success) {
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await logout();
    } catch { }

    try {
      const activeToken = token || await validateCode(code);

      if (activeToken) {
        const data = await resetPassword(formData, activeToken);
        setSuccess(true);
        window.location.href = data?.redirectTo;
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        history.replace('/login');
      } else {
        setError('Código incorreto, verifique novamente.');
      }
    }
    setSubmitting(false);
  }

  const handlePasswordChange = (e) => {
    const value = ref.current.values.password; // Access the password value using the ref

    setPasswordCriteria({
      uppercase: /[A-Z]/.test(value),
      lowercase: /[a-z]/.test(value),
      number: /[0-9]/.test(value),
      length: value.length >= 8
    });
  };

  const [code1, setCode1] = useState(null);
  const [code2, setCode2] = useState(null);
  const [code3, setCode3] = useState(null);
  const [code4, setCode4] = useState(null);

  const isValid = useMemo(() => {
    return code1 && code2 && code3 && code4;
  }, [code1, code2, code3, code4]);


  const [code, setCode] = useState('');

  useEffect(() => {
    setCode(`${code1 || ''}${code2 || ''}${code3 || ''}${code4 || ''}`);
  }, [code1, code2, code3, code4]);


  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const handleConfirmCode = async () => {
    if (!isValid || validatingCode) {
      return;
    }

    setError(null);
    setValidatingCode(true);

    try {
      const validatedToken = await validateCode(code);

      if (validatedToken) {
        setToken(validatedToken);
        setShowPasswordForm(true);
      }
    } catch (err) {
      setError('Código incorreto, verifique novamente.');
    } finally {
      setValidatingCode(false);
    }
  };

  
  return (
    <Container>
      {!showPasswordForm ? (
        <>
          <Text variant="regular">Informe o Código de Acesso de 4 dígitos enviado para o seu e-mail para prosseguir</Text>
          <Spacing size={24} />
          <OutlinedInputContainer>
            <input name="code1" maxLength="1" onChange={e => setCode1(e.target.value)} />
            <input name="code2" maxLength="1" onChange={e => setCode2(e.target.value)} />
            <input name="code3" maxLength="1" onChange={e => setCode3(e.target.value)} />
            <input name="code4" maxLength="1" onChange={e => setCode4(e.target.value)} />
          </OutlinedInputContainer>
          {error && <ErrorText>{error}</ErrorText>}
          <Button
            variant="buttonPrimary"
            text="Confirmar"
            type="button"
            disabled={!isValid || validatingCode}
            loading={validatingCode}
            onClick={handleConfirmCode}
          />
        </>
      ) : (
          <Formik
            initialValues={{ password: '', password_confirmation: '' }}
            validationSchema={schemaPassword}
            validateOnMount
            onSubmit={handleSubmit}
            innerRef={ref}
          >
            {({ isSubmitting, isValid }) => (
              <Form>
                <Text variant="regular">Informe o Código de Acesso de 4 dígitos enviado para o seu e-mail para prosseguir</Text>
                <Spacing size={24} />
                <FormGroup>
                  <Input name="password" type="password" label="Nova senha *" onKeyUp={handlePasswordChange} />
                </FormGroup>
                <Spacing size={24} />
                <FormGroup>
                  <Input name="password_confirmation" type="password" label="Confirmar nova senha" />
                  <ErrorMessage name="password_confirmation" />
                </FormGroup>
                <Spacing size={24} />
                <PasswordRules rules={passwordCriteria} />
                <SubmitButton
                  variant="buttonPrimary"
                  text="Cadastrar nova senha"
                  type="submit"
                  disabled={!isValid || isSubmitting}
                />
              </Form>
            )}
          </Formik>
        )}
    </Container>
  )
}

const PasswordForgot = () => {
  const [modalOk, setModalOk] = useState(false);
  const history = useHistory();

  const [currentStep, setCurrentStep] = useState(1);

  useEffect(() => {
    if (modalOk) setCurrentStep(2);
  }, [modalOk])

  const goback = () => {
    setCurrentStep(1);
    history.goBack();
  };

  return (
    <>
      <ButtonBack onClick={goback} title='Trocar senha' totalSteps={3} step={currentStep} />
      {modalOk ? (
        <Main />
      ) : (
          <Modal onConfirm={() => setModalOk(true)} />
        )}
    </>
  );
}

export default PasswordForgot;
